import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import {
  DiscordEmbedPreview,
  EmbedEditor,
  embedToForm,
  emptyForm,
  formToEmbed,
  type DiscordEmbed,
  type EmbedFormState,
} from '@raynex/discord-ui';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { DiscordCategoryPicker, DiscordRoleMultiPicker } from '@/components/discord/DiscordPickers';
import { ClosePolicyEditor, type Policy } from '@/components/tickets/ClosePolicyEditor';
import { ApiError } from '@/lib/api';
import { useTicketCategories, useUpdateTicketCategory } from '@/lib/queries';

export function CategoryDetailPage() {
  const { id = '' } = useParams();
  const list = useTicketCategories();
  const update = useUpdateTicketCategory();

  const category = list.data?.find((c) => c.id === id);

  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [description, setDescription] = useState('');
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [channelTemplate, setChannelTemplate] = useState('ticket-{user}-{seq}');
  const [supportRoleIds, setSupportRoleIds] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [embedForm, setEmbedForm] = useState<EmbedFormState>(emptyForm());
  const [closePolicy, setClosePolicy] = useState<Policy>({
    triggers: [],
    permissions: { user_can_close: true, support_can_close: true, admin_can_close: true },
    actions: {
      on_warning: 'post_embed_with_extend_button',
      extend_minutes: 1440,
      on_close: 'transcript_then_delete',
    },
    auto_reopen_on_message: false,
  });

  useEffect(() => {
    if (!category) return;
    setName(category.name);
    setEmoji(category.emoji ?? '');
    setDescription(category.description ?? '');
    setTargetCategoryId(category.targetDiscordCategoryId ?? '');
    setChannelTemplate(category.channelNameTemplate);
    setSupportRoleIds(category.supportRoleIds);
    setEnabled(category.enabled);
    setEmbedForm(embedToForm(category.welcomeEmbed as DiscordEmbed));
    if (category.closePolicy && typeof category.closePolicy === 'object') {
      setClosePolicy((prev) => ({ ...prev, ...(category.closePolicy as Partial<Policy>) }));
    }
  }, [category]);

  if (list.isLoading) return <p className="text-muted-foreground">loading…</p>;
  if (!category) {
    return (
      <div>
        <p className="text-muted-foreground">Category not found.</p>
        <Button asChild variant="ghost" size="sm" className="mt-2">
          <Link to="/tickets/categories">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to list
          </Link>
        </Button>
      </div>
    );
  }

  async function handleSave() {
    try {
      await update.mutateAsync({
        id,
        patch: {
          name,
          emoji: emoji || null,
          description: description || null,
          targetDiscordCategoryId: targetCategoryId || null,
          channelNameTemplate: channelTemplate,
          supportRoleIds,
          enabled,
          welcomeEmbed: formToEmbed(embedForm) as Record<string, unknown>,
          closePolicy: closePolicy as unknown as Record<string, unknown>,
        },
      });
      toast.success('Saved');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/tickets/categories">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to list
            </Link>
          </Button>
          <h1 className="mt-2 flex items-center gap-2 text-3xl font-bold tracking-tight">
            {emoji && <span>{emoji}</span>}
            {name}
            <Badge variant={enabled ? 'default' : 'secondary'}>{enabled ? 'on' : 'off'}</Badge>
          </h1>
        </div>
        <Button onClick={handleSave} disabled={update.isPending}>
          <Save className="mr-2 h-4 w-4" />
          Save
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emoji">Emoji</Label>
              <Input
                id="emoji"
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                maxLength={4}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description (admin-only)</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Discord category</Label>
              <DiscordCategoryPicker value={targetCategoryId} onValueChange={setTargetCategoryId} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="channelTemplate">Channel name template</Label>
              <Input
                id="channelTemplate"
                value={channelTemplate}
                onChange={(e) => setChannelTemplate(e.target.value)}
                placeholder="ticket-{user}-{seq}"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Support roles</Label>
            <DiscordRoleMultiPicker value={supportRoleIds} onChange={setSupportRoleIds} />
            <p className="text-xs text-muted-foreground">
              Members with any of these roles see and can manage tickets in this category.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} id="enabled" />
            <Label htmlFor="enabled">Enabled</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Welcome embed</CardTitle>
          <CardDescription>
            Posted in the ticket channel when a user opens a ticket. Variables:{' '}
            <code>{'{user.mention}'}</code>, <code>{'{ticket.id}'}</code>, and any custom field id.
            Modal answers are auto-appended as fields.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <EmbedEditor value={embedForm} onChange={setEmbedForm} />
            <div className="sticky top-4 self-start">
              <DiscordEmbedPreview embed={formToEmbed(embedForm)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Close policy</CardTitle>
          <CardDescription>
            Configure when tickets auto-close, who can close them manually, and what happens to the
            channel afterwards.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ClosePolicyEditor value={closePolicy} onChange={setClosePolicy} />
        </CardContent>
      </Card>
    </div>
  );
}
